// @ts-nocheck
// CAMINHO: src/components/tasks/AddDeadlineModal.tsx
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AddDeadlineModalProps {
  caseId: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: () => void; // Callback para atualizar a lista de prazos
}

// Mesmos presets já usados em tarefas e atendimentos.
const REMINDER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Na hora' },
  { value: 10, label: '10 min antes' },
  { value: 30, label: '30 min antes' },
  { value: 60, label: '1 h antes' },
  { value: 120, label: '2 h antes' },
  { value: 1440, label: '1 dia antes' },
  { value: 2880, label: '2 dias antes' },
];

export const AddDeadlineModal = ({ caseId, isOpen, onOpenChange, onSuccess }: AddDeadlineModalProps) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Média');
  const [description, setDescription] = useState('');
  const [pushNotify, setPushNotify] = useState(true);
  // Default: 1 dia antes + na hora (típico para prazo processual).
  const [reminderOffsets, setReminderOffsets] = useState<number[]>([0, 1440]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const toggleOffset = (value: number) => {
    setReminderOffsets((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value].sort((a, b) => a - b),
    );
  };

  const resetForm = () => {
    setTitle('');
    setDueDate('');
    setPriority('Média');
    setDescription('');
    setPushNotify(true);
    setReminderOffsets([0, 1440]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !dueDate) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha o título e a data do prazo.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;

      const dueDateIso = new Date(dueDate).toISOString();

      const { data: inserted, error } = await supabase
        .from('tasks')
        .insert({
          case_id: caseId,
          title,
          due_date: dueDateIso,
          priority,
          description,
          status: 'Pendente',
          user_id: userId,
          reminder_offsets_minutes: pushNotify ? reminderOffsets : [],
          reminder_sent_offsets: [],
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: 'Sucesso!', description: 'Novo prazo adicionado.' });

      // Push imediato confirmando a criação (ignora silenciosamente em localhost).
      try {
        const isLocalhost =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        if (!isLocalhost && userId && inserted?.id) {
          await fetch('/api/notifications/notify-on-create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'deadline',
              itemId: inserted.id,
              userId,
              title,
              dueDate: dueDateIso,
              caseId,
            }),
          });
        }
      } catch (notifErr) {
        console.warn('[AddDeadlineModal] Falha ao disparar push imediato:', notifErr);
      }

      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err: any) {
      toast({ title: 'Erro ao salvar prazo', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Prazo</DialogTitle>
          <DialogDescription>
            Preencha os detalhes abaixo para registrar um novo prazo ou tarefa para este processo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">Título</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="col-span-3"
                placeholder="Ex: Apresentar contestação"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="due-date" className="text-right">Data</Label>
              <Input
                id="due-date"
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="priority" className="text-right">Prioridade</Label>
              <Select onValueChange={setPriority} defaultValue={priority}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione a prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Baixa">Baixa</SelectItem>
                  <SelectItem value="Média">Média</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">Descrição</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                placeholder="Detalhes adicionais (opcional)"
              />
            </div>

            {/* Notificação push com múltiplos lembretes */}
            <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Notificar por push</p>
                    <p className="text-xs text-muted-foreground">
                      Receber alerta no celular / desktop
                    </p>
                  </div>
                </div>
                <Switch checked={pushNotify} onCheckedChange={setPushNotify} />
              </div>

              {pushNotify && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Quando lembrar (você pode escolher mais de um)
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {REMINDER_OPTIONS.map((opt) => {
                      const active = reminderOffsets.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleOffset(opt.value)}
                          className={cn(
                            'text-xs px-3 py-1.5 rounded-full border transition-all',
                            active
                              ? 'bg-primary text-primary-foreground border-primary shadow'
                              : 'bg-background text-foreground border-input hover:bg-muted',
                          )}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {reminderOffsets.length === 0 && (
                    <p className="text-xs text-amber-600">
                      Nenhum lembrete selecionado — nenhum push será enviado para esse prazo.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Prazo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
